<template>
  <div class="player-list-table" v-loading="loading">
    <el-table
      ref="table"
      row-key="id"
      v-if="rows && rows.length > 0"
      :data="rows"
      @row-click="onRowClick"
      @sort-change="doChangeSort"
    >
      <!-- <el-table-column type="selection" width="55"></el-table-column> -->

      <!-- Name -->
      <el-table-column 
        sortable="custom" 
        :label="i18n('entities.player.fields.name')" 
        min-width="100"
      >
        <template slot-scope="scope">
          <div class="player-list-table__name">
            <div
              class="list__avatar"
              v-if="scope.row.picture.length > 0"
              :style="{ backgroundImage: getPlayerAvatar(scope.row) }"
            />
            <el-tooltip
              :disabled="!hasPermissionToAuditLogs"
              effect="dark"
              :content="scope.row.id"
              placement="bottom"
            >
              <span
                class="list__avatar__name"
                v-if="scope.row.firstName && scope.row.lastName"
                v-text="`${presenter(scope.row, 'firstName')} ${presenter(scope.row, 'lastName')}`"
              />
              <span v-else v-text="'-'" />
            </el-tooltip>
          </div>
        </template>
      </el-table-column>

      <!-- Gender -->
      <el-table-column
        :label="fields.gender.label"
        :prop="fields.gender.name"
        min-width="50"
      >
        <template slot-scope="scope">
          <span
            v-if="scope.row.gender"
            v-text="presenter(scope.row, 'gender')"
          />
          <span
            v-else
            v-text="'-'"
          />
        </template>
      </el-table-column>

      <!-- Birthdate -->
      <el-table-column
        :label="fields.birthdate.label"
        :prop="fields.birthdate.name"
        min-width="70"
      >
        <template slot-scope="scope">
          <span
            v-if="scope.row.birthdate"
            v-text="presenter(scope.row, 'birthdate')"
          />
          <span
            v-else
            v-text="'-'"
          />
        </template>
      </el-table-column>

      <!-- Teams -->
      <el-table-column
        :label="fields.teams.label"
        :prop="fields.teams.name"
        min-width="100"
      >
        <template slot-scope="scope">
          <app-i18n
            v-if="!scope.row.teams || !scope.row.teams.length"
            code="common.none"
          />
          <span
            v-else-if="scope.row.teams.length === 1"
            v-text="scope.row.teams[0].name"
          />

          <el-tooltip
            v-else
            effect="dark"
            :content="scope.row.teams.map(team => team.name).join('\n')"
            placement="bottom"
          >
            <app-i18n code="entities.team.list.multipleTeams" />
          </el-tooltip>

        </template>
      </el-table-column>

      <!-- Last login -->
      <el-table-column
        :label="fields.lastLogin.label"
        :prop="fields.lastLogin.name"
        :resizable="false"
        min-width="70"
        sortable="custom"
      >
        <template slot-scope="scope">
          <span
            v-if="scope.row.lastLogin"
            v-text="presenter(scope.row, 'lastLogin')"
          />
          <app-i18n
            v-else
            code="common.never"
          />
        </template>
      </el-table-column>

      <!-- Actions -->
      <el-table-column
        align="center"
        v-if="!isDisabledAddTeams && !isPropertyNBA"
        min-width="120"
      >
        <template slot-scope="scope">
          <div class="player-list-table__table-actions">
            <!-- Update teams -->
             <player-update-teams
                :key="scope.row.id"
                :hasPermission="hasPermissionToEdit"
                :playerData="scope.row"
                :subpropertyId="subpropertyId"
                :onFinishSubmitFn="onFinishUpdateTeams"
              />
            <!-- <el-button
              v-if="hasPermissionToEdit"
              class="app-content-create-button"
              type="primary"
              @click="handleAddTeam()"
            >
              <app-i18n code="entities.player.updateTeams.action" />
            </el-button> -->

            <!-- Edit -->
            <!-- <router-link :to="`/subproperty/people/player/${scope.row.id}/edit`" v-if="hasPermissionToEdit">
              <el-button
                icon="icon-pen"
                circle
              />
            </router-link> -->

            <!-- Destroy -->
            <!-- <el-button
              v-if="hasPermissionToDestroy"
              :disabled="destroyLoading"
              @click="doDestroyWithConfirm(scope.row.id)"
              circle
              icon="icon-trash"
            /> -->
          </div>
        </template>
      </el-table-column>
      
    </el-table>

    <app-empty-page
      v-else
      :icon-class="'icon-player'"
      :empty-text="i18n('entities.player.empty')"
    />

    <div class="el-pagination-wrapper" v-if="rows && rows.length > 0"> 
      <el-pagination
        :current-page="pagination.currentPage || 1"
        :disabled="loading"
        :layout="paginationLayout"
        :total="count"
        @current-change="doChangePaginationCurrentPage"
        @size-change="doChangePaginationPageSize"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import { AuditLogPermissions } from '@modules/audit-log/audit-log-permissions';
import { PlayerModel } from '@modules/player/player-model';
import { mapGetters, mapActions } from 'vuex';
import { PlayerPermissions } from '@modules/player/player-permissions';
import { i18n } from '@shared/i18n';
import PlayerUpdateTeams from '@modules/player/components/player-update-teams.vue';

const IS_DISABLED_ACTIONS_COLUMN = true;

export default {
  name: 'app-player-list-table',

  computed: {
    ...mapGetters({
      rows: 'player/list/rows',
      count: 'player/list/count',
      loading: 'player/list/loading',
      pagination: 'player/list/pagination',
      isMobile: 'layout/isMobile',
      currentUser: 'auth/currentUser',
      currentManage: 'auth/manage/currentManage',
      destroyLoading: 'player/destroy/loading',
      paginationLayout: 'layout/paginationLayout',
      isPropertyNBA: 'auth/manage/isPropertyNBA',
      dateFormat: 'auth/manage/dateFormat',
      timeFormat: 'auth/manage/timeFormat',
    }),

    hasPermissionToAuditLogs() {
      return new AuditLogPermissions(this.currentUser).read;
    },

    hasPermissionToEdit() {
      return new PlayerPermissions(this.currentUser).edit;
    },

    hasPermissionToDestroy() {
      return new PlayerPermissions(this.currentUser).destroy;
    },

    subpropertyId() {
      const { subproperty: { id } } = this.currentManage;
      if (id) return id;
      else return null;
    },

    fields() {
      const { dateFormat, timeFormat} = this;
      return new PlayerModel({
        dateFormat,
        timeFormat
      }).fields();
    },
    
    isDisabledActionsColumn() {
      return IS_DISABLED_ACTIONS_COLUMN;
    },

    isDisabledAddTeams() {
      return process.env.VUE_APP_DISABLE_PLAYER_ADD_TEAMS === 'true'
    }
  },

  // async mounted() {
  //   this.doFetch();
  // },
  
  methods: {
    ...mapActions({
      doChangeSort: 'player/list/doChangeSort',
      doChangePaginationCurrentPage:
        'player/list/doChangePaginationCurrentPage',
      doChangePaginationPageSize:
        'player/list/doChangePaginationPageSize',
      doDestroy: 'player/destroy/doDestroy',
      doFetch: 'player/list/doFetch',
      filter: 'player/list/filter',
    }),

    presenter(row, fieldName) {
      const { dateFormat, timeFormat } = this;
      return new PlayerModel({ dateFormat, timeFormat }).presenter(row, fieldName);
    },

    async doDestroyWithConfirm(id) {
      try {
        await this.$confirm(
          i18n('common.areYouSure'),
          i18n('common.confirm'),
          {
            confirmButtonText: i18n('common.yes'),
            cancelButtonText: i18n('common.no'),
            type: 'warning',
          },
        );

        return this.doDestroy(id);
      } catch (error) {
        // no
      }
    },

    onRowClick(row, column) {
      if  (!('label' in column)) return;
      this.$router.push({
        name: 'playerView',
        params: {
          id: row.id,
        }
      });
      // this.doManage(row);
    },
    getPlayerAvatar(row) {
      if (!('picture' in row)) return null;
      if (!(row.picture instanceof Array)) return null;
      if (!row.picture.length > 0) return null;
      if (!row.picture[0].publicUrl) return null;
      return `url("${row.picture[0].publicUrl}")`;
    },

    onFinishUpdateTeams(){
       return this.doFetch({
        manage: this.manage,
        filter: this.filter,
      });
    },

    i18n(code) {
      return i18n(code);
    },
  },

  components: {
    [PlayerUpdateTeams.name]: PlayerUpdateTeams,
  },
};
</script>

<style lang="scss" scoped>
.player-list-table {
  &__table-actions {
    display: flex;
  }
  &__name {
    display: grid;
    grid-template-areas: 'img name';
    align-items: center;
  }
}
</style>

<style>
.player-list-table .el-table:not(.el-table--simple) td:last-of-type .cell {
  padding: 0 30px;
}
/* .player-list-table .el-table {
  background-color: red;
} */
</style>
